import "./bootstrap";
import "../css/app.css";

import { createApp, h } from "vue";
import { createInertiaApp, router, usePage } from "@inertiajs/vue3";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import { ZiggyVue } from "../../vendor/tightenco/ziggy/dist/index.esm.js";
import { vMaska } from "maska/vue";
import getDays from "@/helpers/getDays.js";
import colorToHTML from "@/helpers/colorToHTML.js";
import getRouteStatus from "@/helpers/getRouteStatus.js";
import statusToText from "@/helpers/statusToText.js";
import logActionsToText from "@/helpers/logActionTypesToText.js";
import statusToColor from "@/helpers/statusToColor.js";
import neededActionToText from "@/helpers/neededActionToText.js";
import doneActionToText from "@/helpers/doneActionToText.js";
import notifyUser from "@/helpers/notifyUser.js";
import copyToClipboard from "@/helpers/copyToClipboard.js";
import stringToBool from "@/helpers/stringToBool.js";
import downloadFileResponse from "@/helpers/downloadFileResponse.js";
import parseUserCertificates from "@/helpers/sign/parseUserCertificates.js";
import PublicIframeConnector from "@/helpers/PublicIframeConnector.js";
import downloadDocuments from "@/helpers/downloadDocuments.js";
import getAvailableActions from "@/helpers/getAvailableActions.js";
import setCookie from "@/helpers/setCookie.js";
import getCookie from "@/helpers/getCookie.js";
import parseAndValidateCertificates from "@/helpers/sign/parseAndValidateCertificates.js";
import createValidOpensslSignature from "@/helpers/sign/createValidOpensslSignature.js";
import getInitialsOf from "@/helpers/getInitialsOf.js";
import signViaCryptoPro from "@/helpers/sign/signViaCryptoPro.js";
import * as Sentry from "@sentry/vue";
import convertDocxToPdf from "@/helpers/convertDocxToPdf.js";
import statusToDocumentTitle from "@/helpers/statusToDocumentTitle.js";
import getCurrentOrganization from "@/helpers/getCurrentOrganization.js";
import numberWithSpaces from "@/helpers/numberWithSpaces.js";
import getSubscriptionRemainCount from "@/helpers/getSubscriptionRemainCount.js";
import isValidCryptoProSetup from "@/helpers/sign/isValidCryptoProSetup.js";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "../css/driver.css";
import PrimeVue from "primevue/config";
import ConfirmationService from "primevue/confirmationservice";
import Tooltip from "primevue/tooltip";
import { primeVueConfig } from "./primevue-theme.js";
import { addCollection } from "@iconify/vue";
import lucideIcons from "@iconify-json/lucide/icons.json";
import fa6BrandsIcons from "@iconify-json/fa6-brands/icons.json";

// Register the icon collections offline so <Icon icon="lucide:..."> resolves from
// the bundle instead of fetching each icon from api.iconify.design at runtime —
// the runtime fetch leaves empty <svg> placeholders whenever that API is
// unreachable (offline, restricted/corporate network, CSP). Keep this list in
// sync with the icon prefixes actually used in templates.
addCollection(lucideIcons);
addCollection(fa6BrandsIcons);

const appName = import.meta.env.VITE_APP_NAME || "Добыто";

// Vite do not generate polyfill for this function, so we do it by ourselves.
if (!Array.prototype.at) {
    Array.prototype.at = function (n) {
        n = Math.trunc(n) || 0;
        if (n < 0) n += this.length;
        if (n < 0 || n >= this.length) return undefined;
        return this[n];
    };
}

createInertiaApp({
    title: (title) => `${appName} | ${title}`,
    resolve: (name) => {
        const moduleGlob = import.meta.glob("../../modules/*/resources/js/Pages/**/*.vue");
        const match = name.match(/^([A-Z][a-zA-Z0-9]+)\/(.+)$/);
        if (match) {
            const modulePath = `../../modules/${match[1]}/resources/js/Pages/${match[2]}.vue`;
            if (moduleGlob[modulePath]) {
                return resolvePageComponent(modulePath, moduleGlob);
            }
        }
        return resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob("./Pages/**/*.vue"));
    },
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });

        const allowUrls = [/^(http:\/\/)?localhost/, /^https:\/\/[a-zA-Z0-9]\.dobyto\.ru\//, /^\//];
        Sentry.init({
            app,
            dsn: props.initialPage.props?.sentry?.dsn,
            // Third-party DOM recorder (Session Replay / webvisor) chokes on an
            // extension-injected numeric attribute; only triggered by the CryptoPro
            // "no plugin" rejection on pages that never sign. No source-code fix exists.
            ignoreErrors: [/Invalid qualified name/],
            integrations: [
                Sentry.replayIntegration({
                    unblock: [".sentry-unblock, [data-sentry-unblock]"],
                    unmask: [".sentry-unmask, [data-sentry-unmask]"],
                }),
                Sentry.browserTracingIntegration({
                    shouldCreateSpanForRequest(url) {
                        for (let allow of allowUrls) {
                            if (allow.test(url)) {
                                return true;
                            }
                        }

                        return false;
                    },
                }),
            ],
            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: 1.0,
            tracesSampleRate: props.initialPage.props?.sentry?.traces_sample_rate,
            environment: props.initialPage.props?.sentry?.environment,
            // trackComponents moved to tracingOptions: in v10 the vueIntegration reads
            // options.tracingOptions (not top-level) for component lifecycle tracing.
            tracingOptions: { trackComponents: true },
            release: props.initialPage.props?.sentry?.release,
            tracePropagationTargets: allowUrls,
        });

        const setUserContext = (props) => {
            const auth = props.auth;
            if (auth?.user) {
                Sentry.setUser({
                    id: auth.user.id,
                    email: auth.user.email,
                });
                if (auth.teams.current?.id) {
                    Sentry.setTag("team_id", auth.teams.current?.id);
                }
                if (auth.teams.current?.current_organization_id) {
                    Sentry.setTag("organization_id", auth.teams.current?.current_organization_id);
                }
            }
        };

        setUserContext(props.initialPage.props);
        router.on("finish", () => {
            setUserContext(usePage().props);
        });

        app.config.globalProperties.$driver = driver;

        return app
            .use(plugin)
            .use(ZiggyVue)
            .use(PrimeVue, primeVueConfig)
            .use(ConfirmationService)
            .mixin({
                methods: {
                    getDays,
                    colorToHTML,
                    getRouteStatus,
                    statusToText,
                    logActionsToText,
                    statusToColor,
                    neededActionToText,
                    doneActionToText,
                    notifyUser,
                    copyToClipboard,
                    stringToBool,
                    downloadFileResponse,
                    parseUserCertificates,
                    PublicIframeConnector,
                    downloadDocuments,
                    getAvailableActions,
                    setCookie,
                    getCookie,
                    parseAndValidateCertificates,
                    createValidOpensslSignature,
                    getInitialsOf,
                    signViaCryptoPro,
                    convertDocxToPdf,
                    statusToDocumentTitle,
                    getCurrentOrganization,
                    numberWithSpaces,
                    getSubscriptionRemainCount,
                    isValidCryptoProSetup,
                },
            })
            .directive("maska", vMaska)
            .directive("tooltip", Tooltip)
            .mount(el);
    },
    progress: {
        color: "#4B5563",
    },
});
